import { FormInstance, Spin, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import {
  APIError,
  SubCategory,
  FormFieldConfig,
  Category,
  Department,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import { AxiosResponse } from "axios";
import {
  CREATE_SUBCATEGORY,
  GET_CATEGORY,
  GET_DEPARTMENT_CATEGORIES,
} from "../gql/categories";
import * as Sentry from "@sentry/browser";
import BaseForm from "../components/Form/BaseForm";
import { GET_DEPARTMENTS } from "../gql/departments";

interface State {
  id?: string;
  name: string;
  isLoading: boolean;
  categories: Category[];
  page: number;
  q: string;
  departments: Department[];
  departmentId?: string;
  categoryId: string | null;
  category?: Category;
  department?: Department;
}

export default class NewSubcategory extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      categories: [],
      page: 1,
      q: "",
      departments: [],
      categoryId: null,
    };
    this.reference = React.createRef<FormInstance>();
    this.createSubcategory = this.createSubcategory.bind(this);
    this.getAllDepartments = this.getAllDepartments.bind(this);
    this.getDepartmentCategories = this.getDepartmentCategories.bind(this);
    this.handleDepartmentSelect = this.handleDepartmentSelect.bind(this);
    this.getCategory = this.getCategory.bind(this);
  }

  componentDidMount(): void {
    const newSubCategorySearchParams = new URLSearchParams(
      window.location.search,
    );
    this.setState(
      {
        categoryId: newSubCategorySearchParams.get("id"),
      },
      () => {
        this.state.categoryId !== null && this.getCategory();
      },
    );
    this.getAllDepartments();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  getCategory = (): void => {
    const { categoryId } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_CATEGORY,
          variables: { id: categoryId },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const category = data.data.category;
            this.setState({
              category,
              department: category?.department,
            });
          } else {
            message.error("Failed to get category data");
          }
        });
      },
    );
  };

  getAllDepartments = (): void => {
    request({
      query: GET_DEPARTMENTS,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        departments: data.departments,
        isLoading: false,
      });
    });
  };

  getDepartmentCategories = async (): Promise<void> => {
    const { departmentId } = this.state;

    this.setState({ isLoading: true });

    await request({
      query: GET_DEPARTMENT_CATEGORIES,
      variables: { departmentId },
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          categories: data?.categories,
          isLoading: false,
        });
      })
      .catch((error) => {
        message.error("Failed To Load Categories");
        Sentry.captureException(error);
      });

    this.setState({ isLoading: false });
  };

  handleDepartmentSelect = async (departmentId: string): Promise<void> => {
    const department = this.state.departments.find(
      (dept) => dept.id === departmentId,
    );
    this.setState({ departmentId: department?.id }, () => {
      this.getDepartmentCategories();
    });
  };

  redirectToViewCategoryPage(): void {
    this.props.history.push(`/subCategories/${this.state.id}`);
  }

  redirectToEditCategoriesPage(): void {
    this.props.history.push(`/categories/${this.state.categoryId}/edit`);
  }

  async createSubcategory(): Promise<void> {
    interface Response {
      subCategory: SubCategory;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "name",
        "sla",
        "slaCreateByEod",
        "categoryId",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_SUBCATEGORY,
        variables: data,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createSubCategory",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.subCategory?.id }, () => {
          message.success("Subcategory created sucessfully");
          this.state.categoryId
            ? this.redirectToEditCategoriesPage()
            : this.redirectToViewCategoryPage();
        });
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to create subCategory - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  render(): JSX.Element | null {
    const { isLoading, categories, departments, category, department } =
      this.state;
    const fields: FormFieldConfig[] = [
      {
        name: "departmentId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Department",
        initialValue: department?.id,
        placeholder: "Department",
        changeHandler: this.handleDepartmentSelect,
        selectHandler: this.handleDepartmentSelect,
        selectOptions: departments,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "categoryId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Category",
        placeholder: "Category",
        initialValue: category?.id,
        selectOptions: categories,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "name",
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        placeholder: "Name",
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "sla",
        name: "sla",
        rule: "integer",
        inputType: "number",
        required: true,
        label: "SLA",
        placeholder: "SLA",
        elementStyle: {
          width: '100%'
        },
        colConfig: {
          span: 7,
        },
      },
      {
        id: "slaCreateByEod",
        name: "slaCreateByEod",
        rule: "integer",
        inputType: "number",
        required: true,
        initialValue: 17,
        label: "Calculate SLA Before",
        elementStyle: {
          width: '100%'
        },
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "createSubcategory",
        name: "createSubcategoryButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.createSubcategory,
      },
    ];

    return (
      <Card header="NEW SUBCATEGORY">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
